import type { FileMeta } from '../../../shared/transfer.js';
import type { FileSink } from './file-sink.js';
import { contentDisposition } from './file-sink.js';

/*
 * Streams the file through the service worker as a regular browser download.
 * Protocol over a MessagePort (the worker repeats these literals, see sw.ts):
 *   worker -> page: {type:'ready'} once registered, {type:'pull'} whenever it can take a chunk
 *   page -> worker: {type:'chunk', buffer} (transferred), {type:'close'}, {type:'abort', reason}
 * Chunks are only sent on pull, so the worker never buffers more than the stream's high-water mark.
 */
export const DOWNLOAD_PATH_PREFIX = '/download/';
export const KEEPALIVE_PATH = '/sw-keepalive';
/** Fetches reset the worker's idle timer; port messages do not, so the page pings while streaming. */
export const KEEPALIVE_INTERVAL_MS = 10_000;

export type PortLike = Readonly<{
  postMessage: (message: unknown, transfer: Transferable[]) => void;
  addEventListener: (type: 'message', listener: (event: MessageEvent<unknown>) => void) => void;
}>;

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;

/** Page side of the pull protocol; independent of the DOM so it can be unit tested with a fake port. */
export const createPullSender = (port: PortLike): Readonly<{ write: (chunk: ArrayBuffer) => void; close: () => Promise<void>; abort: (reason: string) => void }> => {
  const queue: ArrayBuffer[] = [];
  let pullPending = false;
  let closing = false;
  let resolveClosed: () => void = () => undefined;
  const closed = new Promise<void>((resolve) => {
    resolveClosed = resolve;
  });

  const flush = (): void => {
    if (!pullPending) {
      return;
    }
    const next = queue.shift();
    if (next !== undefined) {
      pullPending = false;
      port.postMessage({ type: 'chunk', buffer: next }, [next]);
      return;
    }
    if (closing) {
      pullPending = false;
      port.postMessage({ type: 'close' }, []);
      resolveClosed();
    }
  };

  port.addEventListener('message', (event) => {
    if (isRecord(event.data) && event.data['type'] === 'pull') {
      pullPending = true;
      flush();
    }
  });

  return {
    write: (chunk) => {
      queue.push(chunk);
      flush();
    },
    close: () => {
      closing = true;
      flush();
      return closed;
    },
    abort: (reason) => {
      queue.length = 0;
      port.postMessage({ type: 'abort', reason }, []);
    },
  };
};

const waitForReady = (port: MessagePort): Promise<void> =>
  new Promise((resolve) => {
    const onMessage = (event: MessageEvent<unknown>): void => {
      if (isRecord(event.data) && event.data['type'] === 'ready') {
        port.removeEventListener('message', onMessage);
        resolve();
      }
    };
    port.addEventListener('message', onMessage);
  });

export const createServiceWorkerSink = async (worker: ServiceWorker, meta: FileMeta): Promise<FileSink> => {
  const id = crypto.randomUUID();
  const channel = new MessageChannel();
  const ready = waitForReady(channel.port1);
  channel.port1.start();
  worker.postMessage(
    {
      type: 'stream-download',
      id,
      headers: {
        'Content-Type': 'application/octet-stream',
        'Content-Disposition': contentDisposition(meta.name),
        'Content-Length': String(meta.size),
      },
    },
    [channel.port2],
  );
  await ready;

  const sender = createPullSender(channel.port1);
  const keepalive = window.setInterval(() => {
    fetch(KEEPALIVE_PATH, { cache: 'no-store' }).catch((error: unknown) => {
      throw new Error(`Service worker keep-alive failed: ${String(error)}`);
    });
  }, KEEPALIVE_INTERVAL_MS);

  // Navigating a hidden frame to the worker-served URL starts the download without leaving the page.
  const frame = document.createElement('iframe');
  frame.hidden = true;
  frame.src = `${DOWNLOAD_PATH_PREFIX}${id}`;
  document.body.append(frame);

  return {
    kind: 'service-worker',
    write: (chunk) => {
      sender.write(chunk);
      return Promise.resolve();
    },
    close: async () => {
      await sender.close();
      window.clearInterval(keepalive);
      return { kind: 'written', location: 'downloads', savedName: meta.name, file: null };
    },
    abort: (reason) => {
      sender.abort(reason);
      window.clearInterval(keepalive);
      return Promise.resolve();
    },
  };
};
